import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { DotCMSApp, initDotCMS } from 'dotcms';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, map, switchMap, take } from 'rxjs';

import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import {
  getDotCmsApiEndpoint,
  getDotCmsApiToken,
  getDotCmsFooterIdentifier,
  getDotCmsHostId,
} from 'ish-core/store/core/configuration';
import { getServerConfigParameter, isServerConfigurationLoaded } from 'ish-core/store/core/server-config';
import { DomService } from 'ish-core/utils/dom/dom.service';

@Injectable({ providedIn: 'root' })
export class DotCmsService {
  private dotCMSApp: DotCMSApp;
  private dotCmsAuthStatus$ = new ReplaySubject<boolean>(1);
  private isServerConfigLoaded$ = new ReplaySubject<boolean>(1);
  host$ = new ReplaySubject<string>(1);
  footerIdentifier$ = new ReplaySubject<string>(1);
  private _isUvePathAllowed = new BehaviorSubject<boolean>(true);
  UveLoading = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store,
    private domService: DomService,
    private featureToggleService: FeatureToggleService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.serverConfigCheck();
    this.getDotCmsConfiguration$().subscribe(([token, host, hostId, footerIdentifier]) => {
      this.host$.next(host);
      this.getCss(host, hostId);
      this.dotCMSApp = initDotCMS({ token, host, hostId });
      this.dotCmsAuthStatus$.next(true);
      /* this.store.dispatch(
        setDotCmsApiConfiguration({
          apiConfiguration: {
            token,
            host,
            hostId,
          },
        })
      ); */
      //Todo: action loadDotCmsAvailableLanguages needs to be dispatched through effects
      //this.store.dispatch(loadDotCmsAvailableLanguages());
      this.footerIdentifier$.next(footerIdentifier);
    });
  }

  getCss(host: string, hostId: string) {
    if (!SSR) {
      const link = this.domService.createElement<HTMLLinkElement>('link', this.document.head);
      this.domService.setProperty(link, 'rel', 'stylesheet');
      this.domService.setProperty(link, 'type', 'text/css');
      this.domService.setProperty(link, 'href', `${host}/application/${hostId}/styles.dotsass?host_id=${hostId}`);
    }
  }

  getDotCmsServerConfig$() {
    const cmsEndpoint$ = this.store.pipe(select(getServerConfigParameter<string>('dotcms.endPoint')));
    const cmsToken$ = this.store.pipe(select(getServerConfigParameter<string>('dotcms.apiToken')));
    const cmsHostId$ = this.store.pipe(select(getServerConfigParameter<string>('dotcms.hostId')));
    const cmsFooterIdentifier$ = this.store.pipe(select(getServerConfigParameter<string>('dotcms.footerIdentifier')));
    return this.getCombinedObservable$(cmsToken$, cmsEndpoint$, cmsHostId$, cmsFooterIdentifier$);
  }

  getDotCmsEnvironmnetConfig$() {
    const cmsEndpoint$ = this.store.pipe(select(getDotCmsApiEndpoint));
    const cmsToken$ = this.store.pipe(select(getDotCmsApiToken));
    const cmsHostId$ = this.store.pipe(select(getDotCmsHostId));
    const cmsFooterIdentifier$ = this.store.pipe(select(getDotCmsFooterIdentifier));
    return this.getCombinedObservable$(cmsToken$, cmsEndpoint$, cmsHostId$, cmsFooterIdentifier$);
  }

  getCombinedObservable$(
    cmsEndpoint$: Observable<string>,
    cmsToken$: Observable<string>,
    cmsHostId$: Observable<string>,
    cmsFooterIdentifier$: Observable<string>
  ) {
    return combineLatest([cmsEndpoint$, cmsToken$, cmsHostId$, cmsFooterIdentifier$]);
  }

  getDotCmsConfiguration$() {
    return this.isServerConfigLoaded$.pipe(
      take(1),
      switchMap(() =>
        combineLatest([this.getDotCmsServerConfig$(), this.getDotCmsEnvironmnetConfig$()]).pipe(
          map(
            ([
              [
                dotCmsServerConfigToken,
                dotCmsServerConfigEndPoint,
                dotCmsServerConfigHostId,
                dotCmsServerFooterIdentifier,
              ],
              [
                dotCmsEnvironmentConfigToken,
                dotCmsEnvironmentConfigEndPoint,
                dotCmsEnvironmentConfigHostId,
                dotCmsEnvironmentFooterIdentifier,
              ],
            ]) => [
              dotCmsServerConfigToken ? dotCmsServerConfigToken : dotCmsEnvironmentConfigToken,
              dotCmsServerConfigEndPoint ? dotCmsServerConfigEndPoint : dotCmsEnvironmentConfigEndPoint,
              dotCmsServerConfigHostId ? dotCmsServerConfigHostId : dotCmsEnvironmentConfigHostId,
              dotCmsServerFooterIdentifier ? dotCmsServerFooterIdentifier : dotCmsEnvironmentFooterIdentifier,
            ]
          )
        )
      )
    );
  }

  serverConfigCheck() {
    this.store
      .pipe(select(isServerConfigurationLoaded))
      .subscribe(data => data && this.isServerConfigLoaded$.next(true));
  }

  emitEMANavEvent(url: string) {
    this.dotCMSApp.event.emit({
      name: 'remote-render-edit',
      data: { pathname: url },
    });
  }

  handleClick(event: Event, url: string) {
    if (this.isRunInsideIframe()) {
      const hrefValue = url === undefined ? (event.target as HTMLAnchorElement).href : url;

      if (!hrefValue) {
        return true;
      }
      if (this.isHomePage(hrefValue)) {
        this.emitEMANavEvent('/index');
        return true;
      }

      const hrefSplit = hrefValue.split('/');

      const index = hrefSplit.indexOf('page');

      if (this.isDotcmsPage(hrefValue)) {
        event.preventDefault();
        const subpagePart = hrefSplit.slice(index + 1).join('/');

        if (subpagePart) {
          this.emitEMANavEvent(`/${subpagePart}`);
        }
      }
    }
    return true;
  }

  isRunInsideIframe(): boolean {
    if (!SSR && window.top !== window.self) {
      return true;
    }

    return false;
  }
  isDotcmsPage(href: string) {
    const hrefSplit = href.split('/');

    const index = hrefSplit.indexOf('page');

    if (index !== -1 && index < hrefSplit.length - 1) {
      return true;
    }

    return false;
  }
  isHomePage(href: string) {
    const endsWithHome = href.endsWith('/home');
    if (endsWithHome) {
      return true;
    }
    return false;
  }
  isDotCmsEditView(): boolean {
    if (this.featureToggleService.enabled('dotCms') && this.isRunInsideIframe()) {
      return true;
    }
    return false;
  }

  get isUvePathAllowed(): boolean {
    // eslint-disable-next-line rxjs/no-subject-value
    return this._isUvePathAllowed.value;
  }
  set isUvePathAllowed(value: boolean) {
    this._isUvePathAllowed.next(value);
  }
  isUvePathAllowed$ = this._isUvePathAllowed.asObservable();

  setUveLoading(newValue: boolean) {
    this.UveLoading.next(newValue);
  }
}
